import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  Table,
  Updater,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Checkbox } from '@ui/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShiftRangeRowSelection } from '../../../app-table/lib/hooks/use-shift-range-row-selection';
import {
  getDefaultRowId,
  getGlobalFilterFn,
  handleRowSelectionChange,
} from '../utils/table-helpers';
import { useVisibility } from './use-visiblility';

type UseVirtualTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  rowHeight: number;
  customSelectColumn?: boolean;
  columnPinning?: {
    left?: string[];
    right?: string[];
  };
  rowSelection?: RowSelectionState;
  setRowSelection?: (rowSelection: RowSelectionState) => void;
  getRowId?: (row: TData) => string;
  isTemplate?: boolean;
  defaultSorting?: SortingState;
  extraRowCount?: number;
};

export const useVirtualTable = <TData,>({
  data,
  columns,
  rowSelection,
  setRowSelection,
  getRowId,
  rowHeight,
  customSelectColumn,
  columnPinning,
  isTemplate,
  defaultSorting,
  extraRowCount = 0,
}: UseVirtualTableProps<TData>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [columnSorting, setColumnSorting] = useState<SortingState>(
    defaultSorting ?? [],
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(
    columns.map((c) => c.id!),
  );

  const [_columnFilters, _setColumnFilters] = useState<ColumnFiltersState>([]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const { columnStateSaveId } = useVisibility();
  const {
    handleRowCheckedChange,
    handleSelectCheckboxClick,
    handleSelectCheckboxKeyDown,
  } = useShiftRangeRowSelection<TData>({ rowSelection, setRowSelection });

  const tableColumns = useMemo<ColumnDef<TData, unknown>[]>(() => {
    if (rowSelection !== undefined && setRowSelection && !customSelectColumn) {
      return [
        {
          id: 'select',
          header: ({ table }: { table: Table<TData> }) => (
            <div className="flex size-full items-center justify-center">
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) =>
                  table.toggleAllRowsSelected(!!value)
                }
              />
            </div>
          ),
          cell: ({ row, table }: { row: Row<TData>; table: Table<TData> }) => (
            <div className="flex size-full items-center justify-center">
              <Checkbox
                checked={row.getIsSelected()}
                className="-ml-2"
                disabled={!row.getCanSelect()}
                onCheckedChange={(value) =>
                  handleRowCheckedChange(row, table, value)
                }
                onClick={handleSelectCheckboxClick}
                onKeyDown={handleSelectCheckboxKeyDown}
              />
            </div>
          ),
          size: 50,
        } as ColumnDef<TData, unknown>,
        ...columns,
      ];
    }

    return columns;
  }, [
    columns,
    customSelectColumn,
    handleRowCheckedChange,
    handleSelectCheckboxClick,
    handleSelectCheckboxKeyDown,
    rowSelection,
    setRowSelection,
  ]);

  const _getRowId = useMemo(() => getRowId || getDefaultRowId, [getRowId]);

  const handleColumnVisibilityChange = useCallback(
    (updater: Updater<VisibilityState>) => {
      setColumnVisibility((prev) => {
        const newVisibility =
          typeof updater === 'function' ? updater(prev) : updater;

        if (columnStateSaveId && !isTemplate) {
          localStorage.setItem(
            columnStateSaveId,
            JSON.stringify(newVisibility),
          );
        }

        return newVisibility;
      });
    },
    [],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      columnOrder,
      sorting: columnSorting,
      columnPinning: {
        left: ['select', ...(columnPinning?.left ?? [])],
        right: ['actions', ...(columnPinning?.right ?? [])],
      },
      ...(rowSelection !== undefined ? { rowSelection } : {}),
      columnVisibility,
    },
    enableGlobalFilter: true,
    enableColumnResizing: true,
    autoResetExpanded: false,
    columnResizeMode: 'onChange',
    globalFilterFn: getGlobalFilterFn<TData>(),
    filterFromLeafRows: true,
    defaultColumn: {
      filterFn: 'includesString',
    },
    getRowId: _getRowId,
    onRowSelectionChange: (updater) =>
      handleRowSelectionChange(updater, rowSelection, setRowSelection),
    onSortingChange: setColumnSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: handleColumnVisibilityChange,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length + extraRowCount,
    estimateSize: () => rowHeight,
    getScrollElement: () => tableContainerRef.current,
    overscan: 20,
  });

  useEffect(() => {
    if (columnStateSaveId && !isTemplate) {
      const columnState = localStorage.getItem(columnStateSaveId);
      if (columnState) {
        try {
          const savedVisibility = JSON.parse(columnState);
          setColumnVisibility(savedVisibility);
        } catch (error) {
          console.warn('Failed to parse saved column visibility state:', error);
        }
      }
    }
  }, [columnStateSaveId]);

  return {
    columnOrder,
    setColumnOrder,
    table,
    rows,
    tableContainerRef,
    rowVirtualizer,
  };
};
